/**
 * `<Badge>` — a small status mark. Three variants:
 *
 * - **count** (default) — an accent pill over the top corner of its slotted host
 *   (an icon, a button…): `<Badge content={{ 3 }}><Icon name={{ 'bell' }} /></Badge>`.
 *   The count is announced via `aria-label` on the host; the pill itself is
 *   `aria-hidden` (decorative). `max` caps the display (e.g. `99+`).
 * - **dot** (`variant="dot"`) — a bare 6px status dot over the corner, no text.
 * - **tag** (`variant="tag"`) — a standalone outline label; the slot IS the text
 *   (`<Badge variant={{ 'tag' }}>New</Badge>`), no host overlay.
 *
 * Pure display — lean DOM (a wrapper + one positioned mark for count/dot; just the
 * wrapper for tag). `position` places the mark in any corner (default top-end).
 */

export type BadgeVariant = 'count' | 'dot' | 'tag';
export type BadgePosition = 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start';

export interface BadgeProps {
  /** Which mark to render. Default `'count'`. */
  variant?: BadgeVariant;
  /** The count/text shown in a `count` mark. */
  content?: string | number;
  /** Cap the displayed count (a number over `max` shows `max+`). */
  max?: number;
  /** Corner for the overlaid mark (count/dot). Default `'top-end'`. */
  position?: BadgePosition;
  /** Accessible name for the host (defaults to the count for `count` badges). */
  label?: string;
  /** Extra classes, forwarded onto the container. */
  class?: string;
}

                               
                                                                  
                   
                        
                                                                                
       
            

export interface BadgeContext {
  badgeClass: () => string;
  ariaLabel: () => string | undefined;
  showMark: () => boolean;
  markText: () => string;
}

export function setup(props: BadgeProps): BadgeContext {
  const variant = (): BadgeVariant => props.variant ?? 'count';
  const hasContent = (): boolean => props.content != null && props.content !== '';

  const markText = (): string => {
    if (variant() !== 'count') return '';
    const value: string | number | undefined = props.content;
    if (typeof value === 'number' && props.max != null && value > props.max) return `${props.max}+`;
    return String(value ?? '');
  };

  // count shows only when there's something to show; dot always; tag never (it IS the mark).
  const showMark = (): boolean => (variant() === 'dot' ? true : variant() === 'count' ? hasContent() : false);

  return {
    badgeClass: (): string => {
      const parts: string[] = ['weave-badge'];
      const v: BadgeVariant = variant();
      if (v !== 'count') parts.push(`weave-badge--${v}`);
      const position: BadgePosition = props.position ?? 'top-end';
      if (position.startsWith('bottom')) parts.push('weave-badge--bottom');
      if (position.endsWith('start')) parts.push('weave-badge--start');
      if (props.class) parts.push(props.class);
      return parts.join(' ');
    },
    // Host label: explicit `label`, else the count (so the pill's number is announced).
    ariaLabel: (): string | undefined => {
      if (variant() === 'tag') return undefined;
      return props.label ?? (variant() === 'count' && hasContent() ? markText() : undefined);
    },
    showMark,
    markText,
  };
}

import { defineComponent } from "@weave-framework/runtime/dom";

import { bindAttr, bindText, child, clone, ifBlock, mountChild, template } from "@weave-framework/runtime/dom";

const _t0 = template("<span data-w-1604d5 class=\"weave-badge__mark\" aria-hidden=\"true\"><!----><\/span>");
const _t1 = template("<span data-w-1604d5><!----><!----><\/span>");
function render(ctx, slots) {
  const _r = clone(_t1);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 1);
  bindAttr(_r, "class", () => (ctx.badgeClass()));
  bindAttr(_r, "aria-label", () => (ctx.ariaLabel()));
  { const _sf = slots["default"]; mountChild(_n0, _sf ? _sf() : null); }
  ifBlock(_n1, () => { if (ctx.showMark()) return _b0; return null; })
  return _r;
  function _b0() {
  const _r = clone(_t0);
  const _n0 = child(_r, 0);
  bindText(_n0, () => (ctx.markText()));
  return _r;
}
}

export default defineComponent(render, setup);